import { useCallback } from 'react';

const CACHE_DURATION = 60000; // 1 minute
const LS_PREFIX = 'hm_cache:';

function getLocalStorageKey(key) {
  return `${LS_PREFIX}${key}`;
}

// Create a singleton cache instance
class GlobalCache {
  constructor() {
    this.cache = new Map();
    this.initialized = false;
  }

  get(key) {
    // In-memory fast path
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    // Fallback to localStorage
    try {
      const raw = localStorage.getItem(getLocalStorageKey(key));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const { data, timestamp } = parsed;
        if (typeof timestamp === 'number') {
          // Respect same TTL for persisted cache; adjust if needed
          if (Date.now() - timestamp < CACHE_DURATION) {
            // Hydrate in-memory for quicker subsequent access
            this.cache.set(key, { data, timestamp });
            return data;
          }
        }
        // Even if stale by TTL, return data for warm start if in-memory is empty
        // and let the background prefetch refresh it. This improves first paint.
        if (!cached) {
          this.cache.set(key, { data, timestamp: Date.now() });
          return data;
        }
      }
    } catch {}
    return null;
  }

  set(key, data) {
    const now = Date.now();
    this.cache.set(key, { data, timestamp: now });
    try {
      localStorage.setItem(
        getLocalStorageKey(key),
        JSON.stringify({ data, timestamp: now })
      );
    } catch {}
  }

  invalidate(key) {
    if (key) {
      this.cache.delete(key);
      try { localStorage.removeItem(getLocalStorageKey(key)); } catch {}
    } else {
      this.cache.clear();
      try {
        // Best-effort sweep of our namespace
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(LS_PREFIX)) toRemove.push(k);
        }
        toRemove.forEach(k => localStorage.removeItem(k));
      } catch {}
    }
  }
}

// Create a single instance to be shared
export const globalCache = new GlobalCache();

// Hook for components to use the cache
export function useDataCache() {
  const getCachedData = useCallback((key) => {
    return globalCache.get(key);
  }, []);

  const setCachedData = useCallback((key, data) => {
    globalCache.set(key, data);
  }, []);

  const invalidateCache = useCallback((key) => {
    globalCache.invalidate(key);
  }, []);

  return { getCachedData, setCachedData, invalidateCache };
}